import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { useForm, usePage } from '@inertiajs/react'
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { SharedProps } from '@/types/inertia'

interface GeoResult {
  name: string
  latitude: number
  longitude: number
  country: string
  admin1: string | null
  timezone: string
}

interface UserSettings {
  city_name: string | null
  latitude: number | null
  longitude: number | null
  timezone: string | null
}

interface Props {
  user_settings: UserSettings
}

function SettingsIndex({ user_settings }: Props) {
  const [modalOpen, setModalOpen] = useState(false)

  const hasLocation = user_settings.latitude != null && user_settings.longitude != null

  return (
    <AtlasPage narrow>
      <PageTitle title="Ustawienia" subtitle="Konfiguracja aplikacji" />
      <Separator />

      <div>
        <h2 className="font-display text-lg text-center uppercase tracking-wider">
          Lokalizacja pogodowa
        </h2>
        <p className="text-center text-xs text-frame italic mt-xs">
          Ustaw swoje miasto, aby widzieć pogodę i jej wpływ na rośliny
        </p>
      </div>

      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="mt-md w-full text-center border border-frame/30 bg-paper-dark px-md py-sm cursor-pointer hover:border-frame/60 transition-colors"
      >
        {hasLocation ? (
          <>
            <p className="font-label text-sm text-ink">
              {user_settings.city_name || 'Lokalizacja ustawiona'}
            </p>
            <p className="text-xs text-frame mt-xs">
              {user_settings.latitude?.toFixed(2)}°N, {user_settings.longitude?.toFixed(2)}°E
              {user_settings.timezone && ` \u00b7 ${user_settings.timezone}`}
            </p>
          </>
        ) : (
          <p className="font-label text-sm text-frame italic">
            Kliknij, aby ustawić lokalizację
          </p>
        )}
      </button>

      <Separator />

      <div className="flex items-center justify-center">
        <Button href="/" variant="secondary">Powrót</Button>
      </div>

      <PageNumber number={99} />

      {modalOpen && (
        <LocationModal
          initial={user_settings}
          onClose={() => setModalOpen(false)}
        />
      )}
    </AtlasPage>
  )
}

function LocationModal({ initial, onClose }: { initial: UserSettings; onClose: () => void }) {
  const { errors } = usePage<SharedProps>().props
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeoResult[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { data, setData, patch, processing } = useForm({
    settings: {
      city_name: initial.city_name || '',
      latitude: initial.latitude,
      longitude: initial.longitude,
      timezone: initial.timezone || '',
    },
  })

  const handleSearch = (value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (value.length < 2) {
      setResults([])
      setShowDropdown(false)
      return
    }
    setSearching(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/settings/geocode?query=${encodeURIComponent(value)}`)
        const json = await res.json()
        setResults(json)
        setShowDropdown(json.length > 0)
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  const selectCity = (result: GeoResult) => {
    setData('settings', {
      city_name: result.name,
      latitude: result.latitude,
      longitude: result.longitude,
      timezone: result.timezone,
    })
    setQuery('')
    setResults([])
    setShowDropdown(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch('/settings', { onSuccess: () => onClose() })
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const hasLocation = data.settings.latitude != null && data.settings.longitude != null

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-md"
      style={{ background: 'rgba(58,42,24,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      <div
        className="w-[28rem] p-lg animate-[modal-in_0.2s_ease-out] rounded"
        style={{ background: 'var(--paper)', border: '2px double var(--frame)' }}
      >
        <div className="flex items-center justify-between mb-md">
          <h3 className="font-display text-base uppercase tracking-wider text-ink">Lokalizacja</h3>
          <button onClick={onClose} className="text-frame text-lg hover:text-ink cursor-pointer bg-transparent border-none">
            &times;
          </button>
        </div>

        <Separator />

        <form onSubmit={handleSubmit} className="space-y-md mt-md">
          {/* City search */}
          <div className="relative" ref={dropdownRef}>
            <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
              Wyszukaj miasto
            </label>
            <input
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="np. Warszawa, Kraków..."
              className="form-input w-full"
              autoFocus
            />
            {searching && (
              <span className="absolute right-3 top-[2.1rem] text-xs text-frame italic">
                Szukam...
              </span>
            )}

            {showDropdown && results.length > 0 && (
              <div className="absolute z-10 mt-xs w-full border border-frame/30 bg-paper shadow-page max-h-48 overflow-y-auto">
                {results.map((r, i) => (
                  <button
                    key={`${r.latitude}-${r.longitude}-${i}`}
                    type="button"
                    onClick={() => selectCity(r)}
                    className="block w-full px-md py-sm text-left font-label text-sm text-ink hover:bg-paper-dark transition-colors"
                  >
                    <span className="font-semibold">{r.name}</span>
                    {r.admin1 && <span className="text-frame">, {r.admin1}</span>}
                    {r.country && <span className="text-frame">, {r.country}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected location */}
          {hasLocation && (
            <div className="text-center border border-frame/30 bg-paper-dark px-md py-sm">
              <p className="font-label text-sm text-ink">
                {data.settings.city_name || 'Lokalizacja ustawiona'}
              </p>
              <p className="text-xs text-frame mt-xs">
                {data.settings.latitude?.toFixed(2)}°N, {data.settings.longitude?.toFixed(2)}°E
                {data.settings.timezone && ` \u00b7 ${data.settings.timezone}`}
              </p>
            </div>
          )}

          {errors && Object.keys(errors).length > 0 && (
            <div className="border border-danger/30 bg-danger/10 px-md py-sm">
              {Object.values(errors).flat().map((err, i) => (
                <p key={i} className="text-xs text-danger">{err}</p>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-md">
            <Button type="submit" disabled={processing}>
              Zapisz
            </Button>
            <Button onClick={onClose} variant="secondary">
              Anuluj
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

SettingsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default SettingsIndex
